// Template-Driven Workflow Hook
// Manages the 5-step surgeon workflow state

import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import {
  PODIATRY_DEMO_BY_SCHEDULED_CASE,
  PODIATRY_DEMO_FACILITY,
} from '@/lib/demo/podiatryDemoSeed';
import {
  ORTHOPEDIC_PACKET_DEMO_BY_SCHEDULED_CASE,
  ORTHOPEDIC_PACKET_DEMO_FACILITY,
  mergeOrthopedicDemoCases,
  mergeOrthopedicDemoTemplates,
  shouldUseOrthopedicPacketDemo,
} from '@/lib/demo/orthopedicPatientPackets';
import {
  SurgeonTemplate,
  SurgeonFacility,
  ScheduledCase,
  CaseCptLine,
  Laterality,
  LATERALITY_MODIFIER_MAP,
} from '@/lib/templates/types';
import {
  buildVariableContext,
  renderTemplate,
  applyLateralityToLines,
} from '@/lib/templates/templateVariables';
import { getSecondaryDefaultModifiers } from '@/lib/modifiers/xepsu';
import { applyPreAuthCptAuthoringRepairs } from '@/lib/surgeon-console/preAuthAuthoringBrain';

export interface TemplateWorkflowState {
  step: number; // 1-5
  // Step 1: Patient
  selectedCase: ScheduledCase | null;
  patientSearchQuery: string;
  searchResults: ScheduledCase[];
  isSearching: boolean;
  highComplexity: boolean; // BMI > 40 / severe deformity flag for Mod -22
  bmiValue: number; // Actual BMI number for narrative injection
  // Step 2: Template + Laterality
  selectedTemplate: SurgeonTemplate | null;
  templates: SurgeonTemplate[];
  laterality: Laterality | null;
  // Step 3: Facility
  selectedFacility: SurgeonFacility | null;
  facilities: SurgeonFacility[];
  // Review state
  cptLines: CaseCptLine[];
  renderedNote: string;
  assistantNoteEnabled: boolean;
  /** AI-extracted clinical context from uploaded documents (MRI, clinical notes, etc.) */
  clinicalContext: string | null;
  /** Structured clinical severity data from extract-clinical-severity agent */
  clinicalSeverityData: ClinicalSeverityData | null;
  // Status
  isLoading: boolean;
  isFinalized: boolean;
}

const initialState: TemplateWorkflowState = {
  step: 1,
  selectedCase: null,
  patientSearchQuery: '',
  searchResults: [],
  isSearching: false,
  highComplexity: false,
  bmiValue: 42.0,
  selectedTemplate: null,
  templates: [],
  laterality: null,
  selectedFacility: null,
  facilities: [],
  cptLines: [],
  renderedNote: '',
  assistantNoteEnabled: false,
  clinicalContext: null,
  clinicalSeverityData: null,
  isLoading: false,
  isFinalized: false,
};

function getCaseClinicalText(scheduledCase: ScheduledCase | null, clinicalContext?: string | null): string {
  const docText = (scheduledCase?.pre_loaded_docs || [])
    .map(doc => [doc.type, doc.extracted_text].filter(Boolean).join(': '))
    .join('\n\n');
  return [clinicalContext, scheduledCase?.scheduled_procedure, docText].filter(Boolean).join('\n\n');
}

export function hasExtractableClinicalDocuments(scheduledCase: ScheduledCase | null): boolean {
  return (scheduledCase?.pre_loaded_docs || []).some(doc => Boolean(doc?.extracted_text?.trim()));
}

export function shouldAutoAnalyzeClinicalDocuments(params: {
  selectedCase: ScheduledCase | null;
  step: number;
  clinicalSeverityData: ClinicalSeverityData | null;
  lastAnalyzedCaseId: string | null;
  inFlightCaseId: string | null;
}): boolean {
  const caseId = params.selectedCase?.id || null;
  if (!caseId) return false;
  if (params.step < 2) return false;
  if (params.clinicalSeverityData) return false;
  if (!hasExtractableClinicalDocuments(params.selectedCase)) return false;
  if (params.lastAnalyzedCaseId === caseId) return false;
  if (params.inFlightCaseId === caseId) return false;
  return true;
}

function extractLabeledDate(rawText: string, label: string): string | null {
  const match = rawText.match(new RegExp(`${label}\\s*:\\s*(\\d{1,2}/\\d{1,2}/\\d{4}|\\d{4}-\\d{2}-\\d{2})`, 'i'));
  return match?.[1] || null;
}

function parseClinicalDate(value: string | null): Date | null {
  if (!value) return null;
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
  const us = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) return new Date(Date.UTC(Number(us[3]), Number(us[1]) - 1, Number(us[2])));
  return null;
}

function estimateDurationWeeks(startDate: string | null, endDate: string | null): number | null {
  const start = parseClinicalDate(startDate);
  const end = parseClinicalDate(endDate);
  if (!start || !end) return null;
  const days = Math.abs(Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)));
  return Math.max(1, Math.round(days / 7));
}

function estimateDurationDays(startDate: string | null, endDate: string | null): number | null {
  const start = parseClinicalDate(startDate);
  const end = parseClinicalDate(endDate);
  if (!start || !end) return null;
  return Math.abs(Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)));
}

function clinicalWeekFromElapsedDays(days: number | null): number | null {
  if (typeof days !== 'number' || !Number.isFinite(days)) return null;
  return Math.max(1, Math.floor(days / 7) + 1);
}

function extractTherapyDateRange(rawText: string): { startDate: string; endDate: string } | null {
  const datePattern = String.raw`(\d{1,2}/\d{1,2}/\d{4}|\d{4}-\d{2}-\d{2})`;
  const therapyFirst = rawText.match(new RegExp(String.raw`(?:formal|supervised|in[- ]person|physical therapy|\bpt\b|rehabilitation|therapy)[^.\n]{0,220}(?:from|between)?\s*${datePattern}[^.\n]{0,80}(?:through|to|-|and)\s*${datePattern}`, 'i'));
  if (therapyFirst) return { startDate: therapyFirst[1], endDate: therapyFirst[2] };

  const datesFirst = rawText.match(new RegExp(String.raw`${datePattern}[^.\n]{0,80}(?:through|to|-|and)\s*${datePattern}[^.\n]{0,180}(?:formal|supervised|in[- ]person|physical therapy|\bpt\b|rehabilitation|therapy)`, 'i'));
  if (datesFirst) return { startDate: datesFirst[1], endDate: datesFirst[2] };

  return null;
}

function extractKneeMuaFlexion(rawText: string): string | null {
  const range = rawText.match(/\bflexion[^.\n]{0,140}?(\d{2,3})\s*(?:to|-)\s*(\d{2,3})\s*(?:degrees?|°)?/i);
  if (range) return `${range[1]} to ${range[2]} degrees`;
  const single = rawText.match(/\bflexion[^.\n]{0,140}?(\d{2,3})\s*(?:degrees?|°)/i);
  return single ? `${single[1]} degrees` : null;
}

function extractKneeMuaExtension(rawText: string): string | null {
  const match = rawText.match(/\bextension[^.\n]{0,100}?(\d{1,3})\s*(?:degrees?|°)/i);
  return match ? `${match[1]} degrees` : null;
}

function extractKneeMuaStaticRomDates(rawText: string): string[] {
  const datePattern = /\b(?:\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})\b/g;
  const sentences = rawText
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+/)
    .filter(sentence => /(flexion|extension|range of motion|\brom\b)/i.test(sentence) && /(static|plateau|consecutive|measurements?|remained)/i.test(sentence));
  const dates = sentences.flatMap(sentence => Array.from(sentence.matchAll(datePattern)).map(match => match[0]));
  return Array.from(new Set(dates));
}

function extractKneeMuaAdlImpact(rawText: string): string | null {
  const sentence = rawText
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+/)
    .find(item => /(reciprocal\s+stair|stair\s+climb|stairs|rising\s+from|standard\s+seated|chair|without\s+assistance|activities of daily living|\badl\b)/i.test(item));
  if (!sentence) return null;
  return sentence
    .replace(/^.*?(?:prevents|preventing)\s+/i, '')
    .replace(/^.*?unable\s+to\s+/i, '')
    .replace(/\s+/g, ' ')
    .replace(/[.;]\s*$/, '')
    .trim();
}

function splitClinicalFindings(value: string | null): string[] {
  if (!value) return [];
  return value
    .replace(/\s+/g, ' ')
    .split(/\s*(?=\d+\.\s*)/)
    .map(item => item.replace(/^\d+\.\s*/, '').replace(/[.;]\s*$/, '').trim())
    .filter(Boolean);
}

function extractShoulderMriEvidence(rawText: string): {
  imaging_modality: string;
  imaging_date: string | null;
  severity_grade: string | null;
  specific_findings: string[];
} | null {
  if (!/\bmri\b/i.test(rawText) || !/(shoulder|rotator cuff|supraspinatus|infraspinatus|biceps)/i.test(rawText)) {
    return null;
  }

  const reportHeaderIndex = rawText.search(/\bMRI\s+Right\s+Shoulder\s+Without\s+Contrast\s*\n/i);
  const mriIndex = reportHeaderIndex >= 0 ? reportHeaderIndex : rawText.search(/\bmri\b/i);
  const mriWindow = mriIndex >= 0 ? rawText.slice(mriIndex, mriIndex + 2500) : rawText;
  const datedMatch = rawText.match(/\bmri\b[^.\n]{0,180}?\b(?:dated|date|exam date)\s*:?\s*(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})/i);
  const imagingDate =
    datedMatch?.[1] ||
    extractLabeledDate(mriWindow, 'Exam Date') ||
    null;

  const impressionMatch = mriWindow.match(/Impression:\s*([\s\S]{1,900}?)(?:\n\n|$)/i);
  const narrativeMatch = rawText.match(/\bmri\b[^.\n]{0,180}?\b(?:demonstrates|confirms|shows)\s+([^.\n]+)/i);
  const specificFindings = splitClinicalFindings(impressionMatch?.[1] || narrativeMatch?.[1] || null);
  const findingText = specificFindings.join(' ');

  return {
    imaging_modality: /without contrast/i.test(mriWindow)
      ? 'MRI right shoulder without contrast'
      : 'MRI right shoulder',
    imaging_date: imagingDate,
    severity_grade: /full[- ]thickness|full[- ]width/i.test(findingText)
      ? 'full-thickness full-width supraspinatus tear'
      : null,
    specific_findings: specificFindings,
  };
}

function extractShoulderInjectionEvidence(rawText: string): {
  type: string;
  date: string | null;
  location: string | null;
  outcome: string | null;
} | null {
  if (!/(subacromial|shoulder)[^.\n]{0,120}(injection|corticosteroid)/i.test(rawText)) {
    return null;
  }

  const injectionReportIndex = rawText.search(/Interventional Procedure Note/i);
  const injectionIndex = injectionReportIndex >= 0
    ? injectionReportIndex
    : rawText.search(/(?:subacromial|shoulder)[^.\n]{0,120}(?:injection|corticosteroid)/i);
  const injectionWindow = injectionIndex >= 0 ? rawText.slice(injectionIndex, injectionIndex + 1800) : rawText;
  const responseMatch = injectionWindow.match(/Patient reported only[^.\n]+(?:baseline severe pain|sustained functional improvement)[^.\n]*/i) ||
    injectionWindow.match(/Injection trial failed[^.\n]*/i);

  return {
    type: 'subacromial corticosteroid injection',
    date: extractLabeledDate(injectionWindow, 'Date of Procedure'),
    location: /right/i.test(injectionWindow) ? 'right subacromial space' : 'subacromial space',
    outcome: responseMatch?.[0]?.trim() || null,
  };
}

export function normalizeClinicalSeverityExtraction(rawText: string, data: ClinicalSeverityData): ClinicalSeverityData {
  const next: ClinicalSeverityData = { ...data };
  const entries = [...(next.conservative_therapy || [])];
  const hasFormalPt =
    /(formal|supervised|in[- ]person|physical therapy|\bpt\b|rehabilitation)/i.test(rawText);
  const therapyDateRange = extractTherapyDateRange(rawText);
  const startDate = extractLabeledDate(rawText, 'Therapy Start Date') || therapyDateRange?.startDate || null;
  const endDate = extractLabeledDate(rawText, 'Therapy End Date') || therapyDateRange?.endDate || null;

  if (hasFormalPt && startDate && endDate) {
    const durationWeeks = estimateDurationWeeks(startDate, endDate);
    const existingIndex = entries.findIndex(entry => /\b(pt|physical therapy|therapy|rehab|rehabilitation)\b/i.test(entry.modality || ''));
    const existing = existingIndex >= 0 ? entries[existingIndex] : null;
    const therapyEntry = {
      ...(existing || {}),
      modality: existing?.modality || 'formal in-person supervised physical therapy',
      start_date: existing?.start_date || startDate,
      end_date: existing?.end_date || endDate,
      duration_weeks: existing?.duration_weeks ?? durationWeeks,
      duration: existing?.duration || (durationWeeks ? `${durationWeeks} weeks` : null),
      outcome: existing?.outcome || (
        /conservative therapy has failed|patient has reached a plateau/i.test(rawText)
          ? 'Patient has reached a plateau. Conservative therapy has failed.'
          : null
      ),
    };

    if (existingIndex >= 0) {
      entries[existingIndex] = therapyEntry;
    } else {
      entries.push(therapyEntry);
    }
    next.conservative_therapy = entries;
  }

  const shoulderMri = extractShoulderMriEvidence(rawText);
  if (shoulderMri) {
    next.radiographic_findings = {
      ...(next.radiographic_findings || {}),
      imaging_modality: next.radiographic_findings?.imaging_modality || shoulderMri.imaging_modality,
      imaging_date: next.radiographic_findings?.imaging_date || shoulderMri.imaging_date,
      severity_grade: next.radiographic_findings?.severity_grade || shoulderMri.severity_grade,
      specific_findings: Array.from(new Set([
        ...(next.radiographic_findings?.specific_findings || []),
        ...shoulderMri.specific_findings,
      ])),
    };
  }

  const shoulderInjection = extractShoulderInjectionEvidence(rawText);
  if (shoulderInjection) {
    const injectionHistory = [...(next.injection_history || [])];
    const alreadyMapped = injectionHistory.some(entry =>
      /subacromial|shoulder/i.test(entry.type || '') &&
      (!shoulderInjection.date || entry.date === shoulderInjection.date),
    );
    if (!alreadyMapped) {
      injectionHistory.push(shoulderInjection);
      next.injection_history = injectionHistory;
    }
  }

  const isKneeMuaPacket = /(manipulation under anesthesia|manipulation of (?:the )?knee|arthrofibrosis|post[- ]?tka|total knee arthroplasty|total knee replacement|cpt\s*27570|m25\.661)/i.test(rawText);
  if (isKneeMuaPacket) {
    const ptDurationDays =
      Number(rawText.match(/\b(\d{2,3})\s*(?:day|days)\b/i)?.[1]) ||
      estimateDurationDays(startDate, endDate);
    const ptDurationWeeks =
      clinicalWeekFromElapsedDays(ptDurationDays) ||
      estimateDurationWeeks(startDate, endDate);
    const romFlexion = extractKneeMuaFlexion(rawText) || next.rom_measurements?.flexion || null;
    const romExtension = extractKneeMuaExtension(rawText) || next.rom_measurements?.extension || null;
    const adlImpact = extractKneeMuaAdlImpact(rawText) ||
      next.functional_limitations?.find(item => /(stair|chair|rising|adl|activities of daily living|without assistance)/i.test(item)) ||
      null;
    const staticRomDates = extractKneeMuaStaticRomDates(rawText);

    if (hasFormalPt && startDate && endDate) {
      const existingIndex = entries.findIndex(entry => /\b(pt|physical therapy|therapy|rehab|rehabilitation)\b/i.test(entry.modality || ''));
      const existing = existingIndex >= 0 ? entries[existingIndex] : null;
      const therapyEntry = {
        ...(existing || {}),
        modality: existing?.modality || 'formal supervised physical therapy',
        start_date: existing?.start_date || startDate,
        end_date: existing?.end_date || endDate,
        duration_weeks: existing?.duration_weeks ?? ptDurationWeeks,
        duration: existing?.duration || (
          ptDurationDays && ptDurationWeeks
            ? `${ptDurationDays} days spanning the ${ptDurationWeeks}th clinical week`
            : null
        ),
        outcome: existing?.outcome || (
          /(plateau|static|failed|limited additional expected gains|despite compliant therapy)/i.test(rawText)
            ? 'ROM plateau despite compliant therapy'
            : null
        ),
      };

      if (existingIndex >= 0) entries[existingIndex] = therapyEntry;
      else entries.push(therapyEntry);
      next.conservative_therapy = entries;
    }

    if (romFlexion || romExtension || staticRomDates.length > 0) {
      next.rom_measurements = {
        ...(next.rom_measurements || {}),
        flexion: next.rom_measurements?.flexion || romFlexion,
        extension: next.rom_measurements?.extension || romExtension,
        other_measurements: Array.from(new Set([
          ...(next.rom_measurements?.other_measurements || []),
          ...staticRomDates.map(date => `${date} flexion ${romFlexion || 'documented'}`),
        ])),
      };
    }

    if (adlImpact) {
      const limitations = new Set(next.functional_limitations || []);
      limitations.add(adlImpact);
      next.functional_limitations = Array.from(limitations);
    }

    if (ptDurationDays || ptDurationWeeks || startDate || endDate || romFlexion || romExtension || adlImpact || staticRomDates.length > 0) {
      next.knee_mua = {
        ...(next.knee_mua || {}),
        pt_duration_days: next.knee_mua?.pt_duration_days ?? ptDurationDays ?? null,
        pt_duration_weeks: next.knee_mua?.pt_duration_weeks ?? ptDurationWeeks ?? null,
        pt_start_date: next.knee_mua?.pt_start_date || startDate || null,
        pt_end_date: next.knee_mua?.pt_end_date || endDate || null,
        rom_flexion: next.knee_mua?.rom_flexion || romFlexion,
        rom_extension: next.knee_mua?.rom_extension || romExtension,
        adl_impact: next.knee_mua?.adl_impact || adlImpact,
        static_rom_dates: next.knee_mua?.static_rom_dates?.length ? next.knee_mua.static_rom_dates : staticRomDates,
      };
    }
  }

  const bmiMatch = rawText.match(/\b(?:BMI|body mass index)\b\s*(?:is|=|of|:)?\s*(\d+(?:\.\d+)?)/i);
  if (bmiMatch && next.bmi == null) {
    const bmi = Number(bmiMatch[1]);
    if (Number.isFinite(bmi)) next.bmi = bmi;
  }

  if (/(obstructive sleep apnea|sleep apnea|\bOSA\b|CPAP)/i.test(rawText)) {
    const comorbidities = new Set(next.comorbidities || []);
    comorbidities.add('Severe Obstructive Sleep Apnea requiring continuous CPAP');
    if (/airway compromise/i.test(rawText)) {
      comorbidities.add('High risk of airway compromise in an Ambulatory Surgery Center');
    }
    next.comorbidities = Array.from(comorbidities);
  }

  return next;
}

export function useTemplateWorkflow() {
  const MAX_STEPS = 5;
  const [state, setState] = useState<TemplateWorkflowState>(initialState);
  const [activeSpecialty, setActiveSpecialty] = useState<string | null>(null);
  const clinicalAnalysisInFlightCaseIdRef = useRef<string | null>(null);
  const lastAnalyzedClinicalCaseIdRef = useRef<string | null>(null);

  // Load templates and facilities on mount; reload templates when specialty changes
  useEffect(() => {
    loadTemplates(activeSpecialty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSpecialty]);

  useEffect(() => {
    loadFacilities();
  }, []);

  /**
   * Load surgeon templates, optionally filtered by specialty domain
   * (e.g. 'podiatry' / 'orthopedics'). Backend filter prevents specialty
   * cross-pollination per the Specialty Domain Isolation rule.
   */
  const loadTemplates = async (specialty?: string | null) => {
    let query = supabase
      .from('surgeon_templates')
      .select('*')
      .eq('is_active', true)
      .order('template_name');

    if (specialty) {
      query = query.eq('specialty', specialty);
    }

    try {
      const { data, error } = await query;
      if (error) {
        console.warn('[loadTemplates] using demo template fallback:', error);
      }

      const dbTemplates: SurgeonTemplate[] = (data || []).map((d: any) => ({
        ...d,
        primary_cpts: Array.isArray(d.primary_cpts) ? d.primary_cpts : JSON.parse(d.primary_cpts || '[]'),
        secondary_cpts: Array.isArray(d.secondary_cpts) ? d.secondary_cpts : JSON.parse(d.secondary_cpts || '[]'),
        common_addons: Array.isArray(d.common_addons) ? d.common_addons : JSON.parse(d.common_addons || '[]'),
        diagnosis_codes: Array.isArray(d.diagnosis_codes) ? d.diagnosis_codes : JSON.parse(d.diagnosis_codes || '[]'),
      }));

      const templates = shouldUseOrthopedicPacketDemo()
        ? mergeOrthopedicDemoTemplates(dbTemplates, specialty)
        : dbTemplates;
      setState(prev => ({ ...prev, templates }));
    } catch (err) {
      console.warn('[loadTemplates] failed:', err);
      if (shouldUseOrthopedicPacketDemo()) {
        setState(prev => ({
          ...prev,
          templates: mergeOrthopedicDemoTemplates([], specialty),
        }));
      }
    }
  };

  const loadFacilities = async () => {
    const { data } = await supabase
      .from('surgeon_facilities')
      .select('*')
      .eq('is_active', true)
      .order('is_default', { ascending: false });

    if (data) {
      const facilities = data as SurgeonFacility[];
      const demoFacility: SurgeonFacility = {
        id: ORTHOPEDIC_PACKET_DEMO_FACILITY.id,
        surgeon_id: 'demo-surgeon-john-larkin',
        facility_name: ORTHOPEDIC_PACKET_DEMO_FACILITY.name,
        facility_type: ORTHOPEDIC_PACKET_DEMO_FACILITY.type,
        address: null,
        phone: null,
        is_default: facilities.length === 0,
        is_active: true,
        is_demo: true,
      };
      const mergedFacilities = shouldUseOrthopedicPacketDemo() && !facilities.some(f => f.id === demoFacility.id)
        ? [demoFacility, ...facilities]
        : facilities;
      const defaultFacility = mergedFacilities.find(f => f.is_default) || mergedFacilities[0] || null;
      setState(prev => ({
        ...prev,
        facilities: mergedFacilities,
        selectedFacility: defaultFacility,
      }));
    }
  };

  // Enrich scheduled cases with mock pre_loaded_docs for demo
  const enrichWithPreLoadedDocs = useCallback((cases: ScheduledCase[]): ScheduledCase[] => {
    return cases.map(sc => {
      // Podiatry demo seed (Pendelton, Gonzalez, Carter, Jenkins, Vance) — attach
      // mock OCR text so extract-clinical-severity / analyze-pre-auth-risk receive
      // real clinical content. Honors the Zero-Fact Policy: text is the canonical
      // demo payload; no values are fabricated downstream.
      const podiatryProfile = PODIATRY_DEMO_BY_SCHEDULED_CASE[sc.id];
      if (podiatryProfile) {
        return {
          ...sc,
          facility_name: PODIATRY_DEMO_FACILITY.name,
          pre_loaded_docs: [
            {
              type: podiatryProfile.docLabel,
              status: 'verified' as const,
              uploaded_by: 'MA - Progressive Podiatry',
              uploaded_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
              extracted_text: podiatryProfile.mockClinicalDocumentText,
            },
          ],
        };
      }
      const orthopedicProfile = ORTHOPEDIC_PACKET_DEMO_BY_SCHEDULED_CASE[sc.id];
      if (orthopedicProfile) {
        return {
          ...sc,
          facility_name: ORTHOPEDIC_PACKET_DEMO_FACILITY.name,
          pre_loaded_docs: orthopedicProfile.preAuthDocs,
        };
      }
      if (sc.patient_name.toLowerCase().includes('patricia davis')) {
        return {
          ...sc,
          pre_loaded_docs: [
            { type: 'MRI Report', status: 'verified' as const, uploaded_by: 'MA - Jennifer R.', uploaded_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString() },
            { type: 'PT Notes (8 weeks)', status: 'verified' as const, uploaded_by: 'MA - Jennifer R.', uploaded_at: new Date(Date.now() - 2.5 * 60 * 60 * 1000).toISOString() },
          ],
        };
      }
      if (sc.patient_name.toLowerCase().includes('sarah johnson')) {
        return { ...sc, pre_loaded_docs: [] };
      }
      return sc;
    });
  }, []);

  // Patient search (scheduled cases only)
  const searchPatients = useCallback(async (query: string) => {
    setState(prev => ({ ...prev, patientSearchQuery: query, isSearching: true }));

    if (!query.trim()) {
      const { data } = await supabase
        .from('scheduled_cases')
        .select('*')
        .eq('status', 'scheduled')
        .order('scheduled_date');

      const scheduledCases = shouldUseOrthopedicPacketDemo()
        ? mergeOrthopedicDemoCases((data || []) as ScheduledCase[])
        : ((data || []) as ScheduledCase[]);
      setState(prev => ({
        ...prev,
        searchResults: enrichWithPreLoadedDocs(scheduledCases),
        isSearching: false,
      }));
      return;
    }

    const { data } = await supabase
      .from('scheduled_cases')
      .select('*')
      .eq('status', 'scheduled')
      .ilike('patient_name', `%${query}%`)
      .order('scheduled_date');

    const scheduledCases = shouldUseOrthopedicPacketDemo()
      ? mergeOrthopedicDemoCases((data || []) as ScheduledCase[], query)
      : ((data || []) as ScheduledCase[]);
    setState(prev => ({
      ...prev,
      searchResults: enrichWithPreLoadedDocs(scheduledCases),
      isSearching: false,
    }));
  }, [enrichWithPreLoadedDocs]);

  // Select patient from scheduled cases (with optional auto-matched template, laterality, clinical context from OCR)
  const selectPatient = useCallback((
    scheduledCase: ScheduledCase,
    autoMatchedTemplate?: SurgeonTemplate,
    extractedLaterality?: Laterality | null,
    clinicalContext?: string,
    clinicalSeverityData?: ClinicalSeverityData | null,
  ) => {
    setState(prev => {
      // Use auto-matched template if provided, otherwise try name-based matching
      let matchedTemplate: SurgeonTemplate | null = autoMatchedTemplate || null;
      if (!matchedTemplate && scheduledCase.scheduled_procedure) {
        const procLower = scheduledCase.scheduled_procedure.toLowerCase();
        const templatesForMatch = shouldUseOrthopedicPacketDemo()
          ? mergeOrthopedicDemoTemplates(prev.templates)
          : prev.templates;
        matchedTemplate = templatesForMatch.find(t =>
          t.template_name.toLowerCase() === procLower ||
          procLower.includes(t.template_name.toLowerCase()) ||
          t.template_name.toLowerCase().includes(procLower)
        ) || null;
      }

      // Determine laterality: use extracted from OCR text, or infer from procedure name
      let effectiveLaterality: Laterality | null = extractedLaterality ?? null;
      if (!effectiveLaterality && scheduledCase.scheduled_procedure) {
        const procLower = scheduledCase.scheduled_procedure.toLowerCase();
        if (/\bbilateral\b/.test(procLower)) effectiveLaterality = 'bilateral';
        else if (/\bright\b/.test(procLower)) effectiveLaterality = 'right';
        else if (/\bleft\b/.test(procLower)) effectiveLaterality = 'left';
      }

      // Build CPT lines with laterality applied (same path as EHR selection)
      let cptLines: CaseCptLine[] = prev.cptLines;
      let assistantNoteEnabled = false;
      if (matchedTemplate) {
        // Build CPT lines with laterality modifiers applied identically to manual flow
        cptLines = buildCptLinesFromTemplate(matchedTemplate, effectiveLaterality);
        
        // Pre-auth stays clinical and medical-necessity focused. Assistant surgeon
        // capture belongs to post-op revenue reconciliation unless payer policy
        // explicitly requires it before authorization.
        assistantNoteEnabled = false;
        cptLines = applyPreAuthCptAuthoringRepairs({
          cptLines,
          selectedCase: scheduledCase,
          template: matchedTemplate,
          laterality: effectiveLaterality,
          clinicalText: getCaseClinicalText(scheduledCase, clinicalContext),
        });
      }

      // Auto-set facility from scheduled case
      let matchedFacility = prev.selectedFacility;
      if (scheduledCase.facility_id || scheduledCase.facility_name) {
        matchedFacility =
          prev.facilities.find(f => f.id === scheduledCase.facility_id) ||
          prev.facilities.find(f => f.facility_name === scheduledCase.facility_name) ||
          (scheduledCase.facility_name
            ? {
                id: scheduledCase.facility_id || `scheduled-${scheduledCase.id}-facility`,
                surgeon_id: scheduledCase.surgeon_id,
                facility_name: scheduledCase.facility_name,
                facility_type: 'Surgical facility',
                address: null,
                phone: null,
                is_default: false,
                is_active: true,
                is_demo: !!scheduledCase.is_demo,
              }
            : prev.selectedFacility);
      }

      // Hydrate high complexity from template metadata if previously saved
      const savedHighComplexity = matchedTemplate?.metadata
        ? (matchedTemplate.metadata as any).default_high_complexity === true
        : false;

      // Build rendered note with clinical context if available
      let renderedNote = prev.renderedNote;
      if (clinicalContext && matchedTemplate?.narrative_template) {
        const context = buildVariableContext({
          patient: scheduledCase,
          template: matchedTemplate,
          laterality: effectiveLaterality,
          facility: matchedFacility?.facility_name,
          cptLines,
        });
        renderedNote = renderTemplate(matchedTemplate.narrative_template, context);
        // Append extracted clinical context for compliance engine evaluation
        renderedNote += `\n\nClinical Summary (Extracted from uploaded document): ${clinicalContext}`;
      }

      return {
        ...prev,
        selectedCase: scheduledCase,
        selectedTemplate: matchedTemplate,
        selectedFacility: matchedFacility,
        laterality: effectiveLaterality,
        cptLines,
        assistantNoteEnabled,
        renderedNote,
        clinicalContext: clinicalContext || null,
        clinicalSeverityData: clinicalSeverityData ?? (
          prev.selectedCase?.id === scheduledCase.id ? prev.clinicalSeverityData : null
        ),
        highComplexity: savedHighComplexity || prev.highComplexity,
        // Advance to step 2 (user can still change template/laterality there)
        step: 2,
      };
    });
  }, []);

  // Select template
  const selectTemplate = useCallback((template: SurgeonTemplate) => {
    setState(prev => {
      let cptLines = buildCptLinesFromTemplate(template, prev.laterality);
      if (prev.selectedCase) {
        cptLines = applyPreAuthCptAuthoringRepairs({
          cptLines,
          selectedCase: prev.selectedCase,
          template,
          laterality: prev.laterality,
          clinicalText: getCaseClinicalText(prev.selectedCase, prev.clinicalContext),
        });
      }
      return {
        ...prev,
        selectedTemplate: template,
        cptLines,
        assistantNoteEnabled: false,
      };
    });
  }, []);

  // Set laterality
  const setLaterality = useCallback((lat: Laterality | null) => {
    setState(prev => {
      let cptLines = prev.cptLines;
      if (prev.selectedTemplate) {
        cptLines = buildCptLinesFromTemplate(prev.selectedTemplate, lat);
        if (prev.selectedCase) {
          cptLines = applyPreAuthCptAuthoringRepairs({
            cptLines,
            selectedCase: prev.selectedCase,
            template: prev.selectedTemplate,
            laterality: lat,
            clinicalText: getCaseClinicalText(prev.selectedCase, prev.clinicalContext),
          });
        }
      }
      cptLines = applyLateralityToLines(cptLines, lat);

      return { ...prev, laterality: lat, cptLines };
    });
  }, []);

  // Set facility
  const setFacility = useCallback((facility: SurgeonFacility) => {
    setState(prev => ({ ...prev, selectedFacility: facility }));
  }, []);

  // Navigate steps
  const setStep = useCallback((step: number) => {
    setState(prev => ({ ...prev, step }));
  }, []);

  const goNext = useCallback(() => {
    setState(prev => {
      const nextStep = Math.min(prev.step + 1, MAX_STEPS);

      // When entering review step, render the note
      if (nextStep === 3 && prev.selectedTemplate?.narrative_template) {
        const context = buildVariableContext({
          patient: prev.selectedCase,
          template: prev.selectedTemplate,
          laterality: prev.laterality,
          facility: prev.selectedFacility?.facility_name,
          cptLines: prev.cptLines,
        });
        const renderedNote = renderTemplate(prev.selectedTemplate.narrative_template, context);
        return { ...prev, step: nextStep, renderedNote };
      }

      return { ...prev, step: nextStep };
    });
  }, []);

  const goBack = useCallback(() => {
    setState(prev => ({ ...prev, step: Math.max(prev.step - 1, 1) }));
  }, []);

  // CPT line management
  const removeCptLine = useCallback((code: string) => {
    setState(prev => {
      // If removing CPT 09980, also disable assistant note
      const newAssistant = code === '09980' ? false : prev.assistantNoteEnabled;
      return {
        ...prev,
        cptLines: prev.cptLines.filter(l => String(l.code) !== code),
        assistantNoteEnabled: newAssistant,
      };
    });
  }, []);

  const resolveCptConflict = useCallback((params: { keepCode: string; removeCode: string }) => {
    setState(prev => {
      const lateralityModifier = prev.laterality ? LATERALITY_MODIFIER_MAP[prev.laterality] : null;
      const removingPrimary = prev.cptLines.some(line =>
        String(line.code) === params.removeCode && line.section === 'primary',
      );

      const remaining = prev.cptLines
        .filter(line => String(line.code) !== params.removeCode)
        .map(line => {
          if (String(line.code) !== params.keepCode || !removingPrimary) return line;

          const modifiers = lateralityModifier
            ? [lateralityModifier, ...line.modifiers.filter(mod => !['XS', 'XE', 'XP', 'XU', '59', 'RT', 'LT', '50'].includes(mod))]
            : line.modifiers.filter(mod => !['XS', 'XE', 'XP', 'XU', '59'].includes(mod));

          return {
            ...line,
            section: 'primary' as const,
            modifiers: Array.from(new Set(modifiers)),
          };
        });

      const hasPrimary = remaining.some(line => line.section === 'primary');
      const cptLines = hasPrimary || remaining.length === 0
        ? remaining
        : remaining.map((line, index) => index === 0
          ? {
              ...line,
              section: 'primary' as const,
              modifiers: lateralityModifier
                ? [lateralityModifier, ...line.modifiers.filter(mod => !['XS', 'XE', 'XP', 'XU', '59', 'RT', 'LT', '50'].includes(mod))]
                : line.modifiers,
            }
          : line);

      return {
        ...prev,
        cptLines,
      };
    });
  }, []);

  const addCptLine = useCallback((line: CaseCptLine) => {
    setState(prev => {
      let cptLines = [...prev.cptLines, { ...line, code: String(line.code) }];
      if (prev.selectedCase && prev.selectedTemplate) {
        cptLines = applyPreAuthCptAuthoringRepairs({
          cptLines,
          selectedCase: prev.selectedCase,
          template: prev.selectedTemplate,
          laterality: prev.laterality,
          clinicalText: getCaseClinicalText(prev.selectedCase, prev.clinicalContext),
        });
      }
      return {
        ...prev,
        cptLines,
      };
    });
  }, []);

  const toggleModifier = useCallback((cptCode: string, modifier: string) => {
    setState(prev => ({
      ...prev,
      cptLines: prev.cptLines.map(l => {
        if (String(l.code) !== cptCode) return l;
        const has = l.modifiers.includes(modifier);
        return {
          ...l,
          modifiers: has
            ? l.modifiers.filter(m => m !== modifier)
            : [...l.modifiers, modifier],
        };
      }),
    }));
  }, []);

  // Update rendered note
  const updateNote = useCallback((note: string) => {
    setState(prev => ({ ...prev, renderedNote: note }));
  }, []);

  // Remove assistant note (also removes CPT 09980)
  const removeAssistantNote = useCallback(() => {
    setState(prev => ({
      ...prev,
      assistantNoteEnabled: false,
      cptLines: prev.cptLines.filter(l => String(l.code) !== '09980'),
    }));
  }, []);

  // Finalize
  const finalize = useCallback(() => {
    setState(prev => ({ ...prev, isFinalized: true }));
  }, []);

  // Reset
  const reset = useCallback(() => {
    setState({ ...initialState });
    loadTemplates();
    loadFacilities();
  }, []);

  // Can proceed checks
  const canProceed = (() => {
    switch (state.step) {
      case 1: return !!state.selectedCase;
      case 2: return !!state.selectedTemplate && (!state.selectedTemplate.laterality_required || !!state.laterality);
      case 3: return state.cptLines.length > 0;
      case 4: return true;
      case 5: return true;
    }
  })();

  const setHighComplexity = useCallback((val: boolean) => {
    setState(prev => ({ ...prev, highComplexity: val }));
  }, []);

  const setBmiValue = useCallback((val: number) => {
    setState(prev => ({ ...prev, bmiValue: val }));
  }, []);

  const addDiagnosisCode = useCallback((diagnosis: { code: string; description: string }) => {
    setState(prev => {
      if (!prev.selectedTemplate || !diagnosis.code?.trim()) return prev;

      const normalizedCode = diagnosis.code.trim().toUpperCase();
      const alreadyExists = (prev.selectedTemplate.diagnosis_codes || []).some(existing => {
        const existingCode = typeof existing === 'string' ? existing : existing.code;
        return existingCode?.trim().toUpperCase() === normalizedCode;
      });

      if (alreadyExists) return prev;

      return {
        ...prev,
        selectedTemplate: {
          ...prev.selectedTemplate,
          diagnosis_codes: [
            ...(prev.selectedTemplate.diagnosis_codes || []),
            {
              code: normalizedCode,
              description: diagnosis.description?.trim() || normalizedCode,
            },
          ],
        },
      };
    });
  }, []);

  const removeDiagnosisCode = useCallback((code: string) => {
    setState(prev => {
      if (!prev.selectedTemplate || !code?.trim()) return prev;

      const normalizedCode = code.trim().toUpperCase();
      return {
        ...prev,
        selectedTemplate: {
          ...prev.selectedTemplate,
          diagnosis_codes: (prev.selectedTemplate.diagnosis_codes || []).filter(existing => {
            const existingCode = typeof existing === 'string' ? existing : existing.code;
            return existingCode?.trim().toUpperCase() !== normalizedCode;
          }),
        },
      };
    });
  }, []);

  // Trigger extract-clinical-severity for the active patient's pre-loaded docs
  // and persist the result to authorization_requests.clinical_metrics so the
  // post-op WoundCareMatrix and downstream agents can read it.
  const analyzeClinicalDocuments = useCallback(async (): Promise<ClinicalSeverityData | null> => {
    const sc = state.selectedCase;
    if (!sc) return null;
    const docs = (sc as any).pre_loaded_docs as Array<{ extracted_text?: string; type?: string }> | undefined;
    const rawText = (docs || [])
      .map(d => d?.extracted_text || '')
      .filter(Boolean)
      .join('\n\n')
      .trim();
    if (!rawText) return null;
    try {
      const { data, error } = await supabase.functions.invoke('extract-clinical-severity', {
        body: { rawText },
      });
      if (error) throw error;
      const sevData = normalizeClinicalSeverityExtraction(rawText, data as ClinicalSeverityData);
      setState(prev => ({ ...prev, clinicalSeverityData: sevData }));

      // Persist to authorization_requests.clinical_metrics. Zero-Fact Policy:
      // only write fields whose evidence is explicitly present in the text.
      const arId = (sc as any).id;
      if (arId) {
        const metrics: Record<string, unknown> = {};
        const woundMatch = rawText.match(/(\d{1,3}(?:\.\d+)?)\s*sq\s*cm/i);
        if (woundMatch) metrics.wound_size_sq_cm = parseFloat(woundMatch[1]);
        const a1cMatch = rawText.match(/HbA1c\s*[:=]?\s*(\d{1,2}(?:\.\d+)?)\s*%/i);
        if (a1cMatch) metrics.hba1c_percent = parseFloat(a1cMatch[1]);
        const wagnerMatch = rawText.match(/Wagner\s*(?:grade|classification)?\s*[:=]?\s*([0-5])/i);
        if (wagnerMatch) metrics.wagner_grade = parseInt(wagnerMatch[1], 10);
        const abiMatch = rawText.match(/\bABI\s*[:=]?\s*(\d(?:\.\d+)?)/i);
        if (abiMatch) metrics.abi_value = parseFloat(abiMatch[1]);
        const ptEntry = sevData.conservative_therapy?.find(entry =>
          /\b(pt|physical therapy|therapy|rehab|rehabilitation)\b/i.test(entry.modality || ''),
        );
        if (ptEntry?.start_date) metrics.pt_start_date = ptEntry.start_date;
        if (ptEntry?.end_date) metrics.pt_end_date = ptEntry.end_date;
        if (ptEntry?.duration_weeks) metrics.pt_duration_weeks = ptEntry.duration_weeks;
        if (typeof sevData.bmi === 'number') metrics.bmi = sevData.bmi;
        if (sevData.comorbidities?.length) metrics.comorbidities = sevData.comorbidities;
        if (sevData.knee_mua) {
          metrics.knee_mua = sevData.knee_mua;
          if (sevData.knee_mua.rom_flexion) metrics.knee_rom_flexion = sevData.knee_mua.rom_flexion;
          if (sevData.knee_mua.adl_impact) metrics.knee_adl_impact = sevData.knee_mua.adl_impact;
          if (sevData.knee_mua.pt_duration_days) metrics.knee_pt_duration_days = sevData.knee_mua.pt_duration_days;
        }
        if (Object.keys(metrics).length > 0) {
          await supabase
            .from('authorization_requests')
            .update({ clinical_metrics: metrics as any })
            .eq('id', arId);
        }
      }
      return sevData;
    } catch (err) {
      console.warn('[analyzeClinicalDocuments] failed:', err);
      return null;
    }
  }, [state.selectedCase]);

  useEffect(() => {
    const caseId = state.selectedCase?.id || null;
    if (!caseId) return;

    if (!shouldAutoAnalyzeClinicalDocuments({
      selectedCase: state.selectedCase,
      step: state.step,
      clinicalSeverityData: state.clinicalSeverityData,
      lastAnalyzedCaseId: lastAnalyzedClinicalCaseIdRef.current,
      inFlightCaseId: clinicalAnalysisInFlightCaseIdRef.current,
    })) {
      return;
    }

    lastAnalyzedClinicalCaseIdRef.current = caseId;
    clinicalAnalysisInFlightCaseIdRef.current = caseId;

    void analyzeClinicalDocuments().finally(() => {
      if (clinicalAnalysisInFlightCaseIdRef.current === caseId) {
        clinicalAnalysisInFlightCaseIdRef.current = null;
      }
    });
  }, [analyzeClinicalDocuments, state.clinicalSeverityData, state.selectedCase, state.step]);

  return {
    state,
    searchPatients,
    selectPatient,
    selectTemplate,
    setLaterality,
    setFacility,
    setStep,
    goNext,
    goBack,
    removeCptLine,
    addCptLine,
    toggleModifier,
    updateNote,
    removeAssistantNote,
    finalize,
    reset,
    canProceed,
    reloadTemplates: loadTemplates,
    setHighComplexity,
    setBmiValue,
    addDiagnosisCode,
    removeDiagnosisCode,
    resolveCptConflict,
    analyzeClinicalDocuments,
    /** Set the active specialty domain filter for surgeon_templates queries. */
    setActiveSpecialty,
    activeSpecialty,
  };
}

// Helper: Build CPT lines from a template
function buildCptLinesFromTemplate(
  template: SurgeonTemplate,
  laterality: Laterality | null
): CaseCptLine[] {
  const primaryLines: CaseCptLine[] = template.primary_cpts.map(cpt => ({
    code: String(cpt.code),
    description: cpt.description,
    modifiers: laterality ? [LATERALITY_MODIFIER_MAP[laterality]] : [],
    section: 'primary',
    fromTemplate: true,
  }));

  const secondaryLines: CaseCptLine[] = template.secondary_cpts.map(cpt => ({
    code: String(cpt.code),
    description: cpt.description,
    modifiers: getSecondaryDefaultModifiers(cpt.default_modifiers),
    section: 'secondary',
    fromTemplate: true,
  }));

  return [...primaryLines, ...secondaryLines];
}

// Helper: Add CPT 09980 assistant line if not already present
function addAssistantCptLine(lines: CaseCptLine[]): CaseCptLine[] {
  if (lines.some(l => String(l.code) === '09980')) return lines;
  return [
    ...lines,
    {
      code: '09980',
      description: 'Surgical Assistant',
      modifiers: [],
      section: 'secondary',
      fromTemplate: true,
    },
  ];
}
